import { ARB_FILENAMES, parseArbWithLegacyNormalization, validateArbSet } from "./arb";
import { extractMetadataFromArb } from "./metadata";
import { LOCALES, type Locale, type OpenedWorkspace, type WritableFileHandle } from "./types";
import type { FileWriter } from "./save";

async function readRequiredFile(directory: FileSystemDirectoryHandle, filename: string): Promise<string> {
  let handle: FileSystemFileHandle;
  try {
    handle = await directory.getFileHandle(filename);
  } catch {
    throw new Error(`Required file ${filename} was not found in the selected folder.`);
  }
  return (await handle.getFile()).text();
}

function buildWorkspace(
  texts: ReadonlyArray<readonly [Locale, string]>,
  directoryHandle: FileSystemDirectoryHandle,
): OpenedWorkspace {
  const normalized = texts.map(([locale, text]) => [locale, parseArbWithLegacyNormalization(ARB_FILENAMES[locale], text, locale)] as const);
  const arbs = Object.fromEntries(
    normalized.map(([locale, result]) => [locale, result.document]),
  ) as Record<Locale, ReturnType<typeof parseArbWithLegacyNormalization>["document"]>;
  const structuralIssues = validateArbSet(arbs).filter((issue) => issue.level === "error");
  if (structuralIssues.length) throw new Error(structuralIssues.map((issue) => issue.message).join("\n"));

  const metadata = extractMetadataFromArb(arbs.en);
  let metadataNormalized = false;
  const contextValues = new Map<string, Set<string>>();
  for (const [, result] of normalized) {
    for (const [key, context] of Object.entries(result.contexts)) {
      const values = contextValues.get(key) ?? new Set<string>();
      values.add(context);
      contextValues.set(key, values);
    }
  }
  for (const [key, values] of contextValues) {
    if (values.size !== 1) throw new Error(`Legacy context for ${key} differs between locales.`);
    const context = [...values][0];
    if (!context) continue;
    const existing = metadata.entries[key]?.description;
    if (existing && existing !== context) {
      throw new Error(`Legacy context for ${key} conflicts with its @${key} description in app_en.arb.`);
    }
    if (!existing) {
      metadata.entries[key] = { ...(metadata.entries[key] ?? {}), description: context };
      metadataNormalized = true;
    }
  }

  const normalizedLocales = normalized.filter(([, result]) => result.changed).map(([locale]) => locale);
  const normalizationMessages = [...new Set(normalized.flatMap(([, result]) => result.messages))];
  return {
    directoryHandle,
    arbs,
    metadata,
    normalizedLocales,
    metadataNormalized,
    normalizationMessages,
  };
}

export function supportsWritableDirectories(): boolean {
  return typeof window.showDirectoryPicker === "function";
}

export function writableDirectorySupportError(): string | null {
  if (supportsWritableDirectories()) return null;
  if (!window.isSecureContext) {
    return `Writable folder access is blocked on ${window.location.origin} because it is not a secure origin. Open this tool through HTTPS, http://localhost, or http://127.0.0.1.`;
  }
  if (window.top !== window) {
    return "Writable folder access is blocked in this embedded view. Open the editor URL in a normal top-level Chrome or Edge tab.";
  }
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return "Writable folder access requires desktop Chrome or Edge; mobile browsers cannot write back to the selected ARB folder.";
  }
  return "This Chrome or Edge profile does not expose writable folder access. Check chrome://policy for filesystem restrictions or try another desktop profile.";
}

export async function openWorkspaceFolder(): Promise<OpenedWorkspace> {
  const supportError = writableDirectorySupportError();
  if (supportError || !window.showDirectoryPicker) throw new Error(supportError ?? "Writable folder access is unavailable.");
  const directoryHandle = await window.showDirectoryPicker({ id: "wb-localization-data", mode: "readwrite" });
  const texts = await Promise.all(
    LOCALES.map(async (locale) => [locale, await readRequiredFile(directoryHandle, ARB_FILENAMES[locale])] as const),
  );
  return buildWorkspace(texts, directoryHandle);
}

export function directoryWriter(directory: FileSystemDirectoryHandle): FileWriter {
  return {
    async write(filename: string, content: string): Promise<void> {
      const handle = (await directory.getFileHandle(filename)) as WritableFileHandle;
      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();
    },
  };
}
